import React, { RefObject } from 'react';
import { X, CheckCircle, AlertCircle, Clock, Users, Mail, Phone } from 'lucide-react';

interface ProblemStatementSectionProps {
  visibleSections: Set<string>;
  problemSectionRef: RefObject<HTMLElement>;
}

const ProblemStatementSection: React.FC<ProblemStatementSectionProps> = ({
  visibleSections,
  problemSectionRef,
}) => {
  const painPoints = [
    {
      icon: AlertCircle,
      text: 'Lost in design, attire, and guest lists',
    },
    {
      icon: Clock,
      text: 'Invites left to the last minute',
    },
    {
      icon: Users,
      text: 'Manual contact collection and organization',
    },
    {
      icon: Mail,
      text: 'Scattered RSVPs across multiple channels',
    },
    {
      icon: Phone,
      text: 'Constant direct contact between host and guests',
    },
  ];

  const benefits = [
    {
      icon: CheckCircle,
      text: 'Organized guest management from day one',
    },
    {
      icon: CheckCircle,
      text: 'Automated invitation distribution',
    },
    {
      icon: CheckCircle,
      text: 'Centralized RSVP tracking and reporting',
    },
    {
      icon: CheckCircle,
      text: 'No direct contact needed',
    },
    {
      icon: CheckCircle,
      text: 'Professional event website included',
    },
  ];

  return (
    <section
      id="problem-statement"
      ref={problemSectionRef}
      className="relative z-10 bg-black"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none z-0" />

      <div className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <h2
            className={`font-megant text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 text-center transition-all duration-1000 ${
              visibleSections.has('problem-statement')
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            THE EVENT PLANNING CHALLENGE
          </h2>

          {/* Comparison Grid */}
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            {/* Traditional Event Planning - Left Side */}
            <div
              className={`transition-all duration-1000 ease-out ${
                visibleSections.has('problem-statement')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-20'
              }`}
              style={{
                visibility: visibleSections.has('problem-statement') ? 'visible' : 'hidden',
              }}
            >
              <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 rounded-lg border border-red-500/30 p-4 sm:p-6 md:p-8">
                <div className="flex items-center mb-4 sm:mb-6">
                  <X className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 mr-2 sm:mr-3 flex-shrink-0" />
                  <h3 className="font-megant text-xl sm:text-2xl md:text-3xl font-bold text-white">
                    Traditional Event Planning
                  </h3>
                </div>
                <ul className="font-montserrat space-y-4">
                  {painPoints.map((point, index) => {
                    const Icon = point.icon;
                    return (
                      <li
                        key={index}
                        className={`font-montserrat flex items-start transition-all duration-500 ${
                          visibleSections.has('problem-statement')
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 -translate-x-10'
                        }`}
                        style={{
                          transitionDelay: visibleSections.has('problem-statement')
                            ? `${800 + index * 200}ms`
                            : '0ms',
                        }}
                      >
                        <Icon className="w-5 h-5 text-red-400 mr-2 sm:mr-3 mt-1 flex-shrink-0" />
                        <span className="font-montserrat text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                          {point.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* With Mirsal - Right Side */}
            <div
              className={`transition-all duration-1000 ease-out ${
                visibleSections.has('problem-statement')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-20'
              }`}
              style={{
                visibility: visibleSections.has('problem-statement') ? 'visible' : 'hidden',
              }}
            >
              <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 rounded-lg border border-green-400/30 p-4 sm:p-6 md:p-8">
                <div className="flex items-center mb-4 sm:mb-6">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 mr-2 sm:mr-3 flex-shrink-0" />
                  <h3 className="font-megant text-xl sm:text-2xl md:text-3xl font-bold text-white">With Mirsal</h3>
                </div>
                <ul className="font-montserrat space-y-4">
                  {benefits.map((benefit, index) => {
                    const Icon = benefit.icon;
                    return (
                      <li
                        key={index}
                        className={`font-montserrat flex items-start transition-all duration-500 group hover:translate-x-2 ${
                          visibleSections.has('problem-statement')
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 translate-x-10'
                        }`}
                        style={{
                          transitionDelay: visibleSections.has('problem-statement')
                            ? `${800 + index * 200}ms`
                            : '0ms',
                        }}
                      >
                        <Icon className="w-5 h-5 text-green-400 mr-2 sm:mr-3 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="font-montserrat text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                          {benefit.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Connecting Line Animation */}
          <div className="hidden md:block relative mt-8">
            <div
              className={`h-px bg-gradient-to-r from-red-500/50 via-gray-700 to-green-500/50 transition-all duration-1000 ${
                visibleSections.has('problem-statement')
                  ? 'opacity-100 scale-x-100'
                  : 'opacity-0 scale-x-0'
              }`}
              style={{
                transitionDelay: visibleSections.has('problem-statement') ? '1800ms' : '0ms',
              }}
            />
            <div
              className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-black rounded-full border-2 border-green-400 flex items-center justify-center transition-all duration-500 ${
                visibleSections.has('problem-statement')
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-0'
              }`}
              style={{
                transitionDelay: visibleSections.has('problem-statement') ? '2000ms' : '0ms',
              }}
            >
              <span className="text-green-400 text-sm font-bold">→</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="relative z-10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            className={`h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 ${
              visibleSections.has('problem-statement')
                ? 'opacity-100 scale-x-100'
                : 'opacity-0 scale-x-0'
            }`}
          />
        </div>
      </div>
    </section>
  );
};

export default ProblemStatementSection;

